import React, { useState, useEffect } from 'react';
import { Building, AlertTriangle, CheckCircle } from 'lucide-react';
import { getVendorAssessments } from '../../../services/complianceService';


/**
 * Vendor Risk Assessment Panel
 * SOC 2 control for third-party vendor management
 */
const VendorRiskPanel = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadVendors();
  }, []);
  
  const loadVendors = async () => {
    setLoading(true);
    const result = await getVendorAssessments();
    if (result?.success) {
      setVendors(result?.data || []);
    }
    setLoading(false);
  };
  
  const getRiskColor = (riskLevel) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors?.[riskLevel] || colors?.medium;
  };
  
  const isReviewOverdue = (nextReviewDate) => {
    return new Date(nextReviewDate) <= new Date();
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading vendor assessments...</div>
        ) : vendors?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No vendor assessments found</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {vendors?.map(vendor => {
              const reviewOverdue = isReviewOverdue(vendor?.next_review_date);
              
              return (
                <div key={vendor?.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg border ${getRiskColor(vendor?.risk_level)}`}>
                      <Building className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{vendor?.vendor_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {vendor?.vendor_service} | {vendor?.vendor_category}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRiskColor(vendor?.risk_level)}`}>
                            Risk: {vendor?.risk_score}/10 ({vendor?.risk_level?.toUpperCase()})
                          </span>
                          {reviewOverdue && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertTriangle className="w-3 h-3" />
                              Review Overdue
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Data Shared:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {vendor?.data_types_shared?.map((type, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">Compliance:</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {vendor?.soc2_certified && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                <CheckCircle className="w-3 h-3" />
                                SOC 2
                              </span>
                            )}
                            {vendor?.hipaa_compliant && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                <CheckCircle className="w-3 h-3" />
                                HIPAA
                              </span>
                            )}
                            {vendor?.gdpr_compliant && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                <CheckCircle className="w-3 h-3" />
                                GDPR
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Last Review:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {new Date(vendor?.last_review_date)?.toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Next Review:</span>
                          <span className={`ml-2 font-medium ${
                            reviewOverdue ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {new Date(vendor?.next_review_date)?.toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      
                      {vendor?.notes && (
                        <div className="mt-3 text-sm text-gray-600 bg-gray-50 rounded p-3">
                          <strong>Notes:</strong> {vendor?.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorRiskPanel;